import React from 'react';
import { useTranslate } from 'react-polyglot';

import styles from './DiseaseAlert.module.scss';

// «Sykdomsvarsel»: a summary of the diseases in the two pedigrees (the same
// trees and switches as the stamtavle). Orange: the disease is in the dam's
// line, the sire's line or both, a line being its dogs, their full siblings
// and (when switched on) half siblings, as in the stamtavle. Purple: which
// kind of sibling has it. Nothing is shown when there are no findings.
// Carriers (DNA test «Bærer») are not a problem and are left out.

// line: everything in the line; full / half: the siblings alone
function collect(dog: any, showHalf: boolean, line: Set<string>, full: Set<string>, half: Set<string>) {
    if (!dog || dog._skeleton) return;
    const findings = (health: any[] | undefined) => (health ?? []).filter(d => d.status != "baerer");
    for (const d of findings(dog.health)) line.add(d.key);
    for (const sibling of dog.relatives?.full?.affected ?? []) {
        for (const d of findings(sibling.health)) { line.add(d.key); full.add(d.key); }
    }
    if (showHalf) {
        for (const sibling of dog.relatives?.half?.affected ?? []) {
            for (const d of findings(sibling.health)) { line.add(d.key); half.add(d.key); }
        }
    }
    collect(dog.dam, showHalf, line, full, half);
    collect(dog.sire, showHalf, line, full, half);
}

function DiseaseAlert(props: DiseaseAlertProps) {
    const t = useTranslate();
    const hidden = props.hiddenDiseases ?? new Set<string>();

    const showHalf = props.showHalfSiblings ?? false;
    const damOwn = new Set<string>(), sireOwn = new Set<string>(), full = new Set<string>(), half = new Set<string>();
    collect(props.dam, showHalf, damOwn, full, half);
    collect(props.sire, showHalf, sireOwn, full, half);

    const name = (key: string) => t("dog.details.health.status." + key);
    // «Epilepsi» -> «epilepsi» inside a sentence, but «LTV» stays «LTV»
    const inSentence = (key: string) => {
        const n = name(key);
        return n.length > 1 && n[1] == n[1].toLowerCase() ? n[0].toLowerCase() + n.slice(1) : n;
    };
    const k = (key: string, options?: any) => t("selectedrequestbreeding.diseasealert." + key, options);

    const keys = Array.from(new Set([...Array.from(damOwn), ...Array.from(sireOwn), ...Array.from(full), ...Array.from(half)]))
        .filter(key => !hidden.has(key))
        .sort((a, b) => name(a).localeCompare(name(b), "nb"));

    const items: { kind: "line" | "siblings", text: string }[] = [];
    for (const key of keys) {
        if (damOwn.has(key) && sireOwn.has(key)) items.push({ kind: "line", text: k("bothlines", { disease: name(key) }) });
        else if (damOwn.has(key)) items.push({ kind: "line", text: k("damline", { disease: name(key) }) });
        else if (sireOwn.has(key)) items.push({ kind: "line", text: k("sireline", { disease: name(key) }) });
    }
    for (const key of keys) {
        if (full.has(key) && half.has(key)) items.push({ kind: "siblings", text: k("fullandhalf", { disease: inSentence(key) }) });
        else if (full.has(key)) items.push({ kind: "siblings", text: k("full", { disease: inSentence(key) }) });
        else if (half.has(key)) items.push({ kind: "siblings", text: k("half", { disease: inSentence(key) }) });
    }
    if (items.length == 0) return null;

    return (
        <section className={styles.alert}>
            <div className={styles.title}>
                <span className={styles.triangle} aria-hidden="true">!</span>
                {k("title")}
            </div>
            <ul className={styles.items}>
                {items.map(item => (
                    <li key={item.kind + item.text}>
                        <span className={styles.dot + " " + styles[item.kind]}>!</span>
                        {item.text}
                    </li>
                ))}
            </ul>
        </section>
    );
}

class DiseaseAlertProps {
    // Trees from /api/statistics/dog/:id/familytree
    dam?: any;
    sire?: any;
    hiddenDiseases?: Set<string>;
    // As the stamtavle's half sibling switch; off by default
    showHalfSiblings?: boolean;
}

export default DiseaseAlert;
